import { Builder } from '@builder.io/react';
import { useState, useEffect } from 'react';
import moment from 'moment';
import services from '../../../../../services';
import { useSelector } from 'react-redux';
import { selectSelectedCountryCode, selectSelectedCurrency } from '../../../../../store/user';
import { selectProducts } from '../../../../../store/products';
import ProductsTabVertical from '../../../../general/productsTabVertical';

const Top10ProdsWidget = ({ 
  page = 'general', 
  ProductIds = '', 
  palestineProductIds = '', 
  noOrder = true 
}) => {
  const selectedCurrencyStore = useSelector(selectSelectedCurrency);
  const products = useSelector(state => selectProducts(state));
  const [selectedCurrency, setSelectedCurrency] = useState();
  const selectedcountryCode = useSelector(selectSelectedCountryCode);
  const [isLoaded, setIsLoaded] = useState(false);
  const [topProducts, setTopProducts] = useState([0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
  
  // Parse Palestine product IDs from CMS input
  const getPalestineProductIDs = () => {
    try {
      if (!palestineProductIds) {
        // Default IDs if none provided in CMS
        return [
          74, 136, 250, 251, 252, 253, 254, 255, 335, 336, 337, 338, 339, 352, 353, 
          354, 355, 356, 357, 358, 371, 372, 373, 374, 375, 376, 399, 400, 401, 402, 
          403, 404, 405, 406, 409, 480, 481, 482, 546, 547, 548, 549, 550, 551, 552, 
          611, 624, 592, 593, 594, 595, 596, 597, 598, 599, 600, 601, 602, 604, 605, 
          606, 607, 623, 642, 643, 644, 646, 647, 648, 649
        ];
      }
      return palestineProductIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    } catch (error) {
      console.error('Error parsing Palestine product IDs:', error);
      return [];
    }
  };

  const getAllProducts = () => {
    setIsLoaded(true);
    getTop10Products();
  };

  const getAdditionalProducts = (products) => {
    if (!ProductIds || ProductIds.trim() === '') return [];

    const additionalCreators = ProductIds.split(',')
      .map(id => id.trim())
      .filter(id => id);

    let additionalProducts = [];

    products.forEach(product => {
      product.sub_categories.forEach(subCategory => {
        subCategory.products.forEach(prod => {
          if (additionalCreators.includes(prod.creator)) {
            additionalProducts.push(prod);
          }
        });
      });
    });

    return additionalProducts.sort((a, b) => {
      return additionalCreators.indexOf(a.creator) - additionalCreators.indexOf(b.creator);
    });
  };

  const additionalProducts = getAdditionalProducts(products);

  const getTop10Products = async () => {
    let tempTopProducts = [];
    const palestineProductID = getPalestineProductIDs();
    
    const tempGeneralSearch = {
      from: new Date().toISOString().split('T')[0],
      to: new Date().toISOString().split('T')[0],
      host: ['localhost', 'uat.matwproject.org', 'samad-uat.matwproject.org', 'etc'].includes(window.location.hostname)
        ? 'matwproject.org'
        : window.location.hostname,
      limit: 11,
    };
    
    const tempPalestineSearch = {
      from: moment().subtract(7, 'days').format('YYYY-MM-DD'),
      host: ['localhost', 'uat.matwproject.org', 'samad-uat.matwproject.org', 'etc'].includes(window.location.hostname)
        ? 'matwproject.org'
        : window.location.hostname,
      limit: 11,
      products: palestineProductID,
    };
    
    const tempSearch = page === 'general' ? tempGeneralSearch : tempPalestineSearch;

    try {
      const response = await services.getTop20Products(tempSearch);
      
      let temp = response.data.filter(item => item.product_name !== 'Admin Contribution');
      
      const compare = (a, b) => b.total_sales - a.total_sales;
      const sortable = temp.sort(compare);
      const _top10 = sortable.slice(0, 11);

      products.forEach(item => {
        item.sub_categories.forEach(inner_item => {
          inner_item.products.forEach(inner_item_product => {
            _top10.forEach((_item, index) => {
              if (inner_item_product.name === _item.product_name) {
                tempTopProducts[index] = inner_item_product;
              }
            });
          });
        });
      });

      const uniqBy = (a) => {
        const seen = {};
        return a.filter(item => {
          const k = item.creator;
          return seen.hasOwnProperty(k) ? false : (seen[k] = true);
        });
      };

      let tempNewProducts = uniqBy(tempTopProducts).slice(0, 10).filter(Boolean);
      const combinedProd = [...additionalProducts, ...tempNewProducts].reduce((unique, item) => {
        return unique.some(i => i.creator === item.creator) ? unique : [...unique, item];
      }, []);

      setTopProducts(combinedProd);
    } catch (error) {
      console.error('Error fetching top products:', error);
    }
  };

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  useEffect(() => {
    products && Object.keys(products).length && !isLoaded && getAllProducts();
  }, [products]);

  useEffect(() => {
    if (products && Object.keys(products).length) {
      getAllProducts();
    }
  }, [ProductIds]);

  useEffect(() => {
    if (products && Object.keys(products).length) {
      setIsLoaded(false);
      getAllProducts();
    }
  }, [noOrder, page]); // Added page as dependency
  
  useEffect(() => {
    selectedCurrencyStore ? setSelectedCurrency(selectedCurrencyStore) : setSelectedCurrency('AUD');
  }, [selectedCurrencyStore]);

  return (
    <div>
      <ProductsTabVertical
        sections={[
          {
            products: topProducts.filter(prod => prod.name !== 'Pakistan Solar Powered Well'),
            noOrder,
          },
        ]}
      />
    </div>
  );
};

Builder.registerComponent(Top10ProdsWidget, {
  name: 'Top 10 Products Widget',
  inputs: [
    {
      name: 'page',
      type: 'string',
      enum: [
        { label: 'General', value: 'general' },
        { label: 'Palestine', value: 'palestine' }
      ],
      defaultValue: 'general',
      helperText: 'Select "Palestine" to show Palestine-specific products'
    },
    {
      name: 'palestineProductIds',
      type: 'string',
      helperText: 'Comma-separated list of product IDs for Palestine page',
      defaultValue: '74,136,250,251,252,253,254,255,335,336,337,338,339,352,353,354,355,356,357,358,371,372,373,374,375,376,399,400,401,402,403,404,405,406,409,480,481,482,546,547,548,549,550,551,552,611,624,592,593,594,595,596,597,598,599,600,601,602,604,605,606,607,623,642,643,644,646,647,648,649',
      showIf: options => options.get('page') === 'palestine'
    },
    {
      name: 'ProductIds',
      type: 'string',
      helperText: 'Optional: Comma-separated list of product creator IDs to display before the top 10 products',
      defaultValue: '',
    },
    {
      name: 'noOrder',
      type: 'boolean',
      defaultValue: true,
      helperText: 'If true, the products will not be ordered. If false, the products will be ordered by Amount from highest to lowest.',
    },
  ],
});